import type { useI18n } from '~/hooks/use-i18n';

import type { CatalogCarouselItem } from '../models/catalog-carousel-item';

type TranslateFn = ReturnType<typeof useI18n>['t'];

export type GetDefaultCatalogCarouselItemsOptions = {
  /**
   * When true, omits the standard Agentic AI Nanodegree (nd900) card so it is not
   * shown next to the free-trial hero slide for the same program.
   */
  omitAgenticAiNanodegree?: boolean;
};

const CATALOG_AGENTIC_AI_NANODEGREE_CAROUSEL_KEY = 'nd900';

export function getDefaultCatalogCarouselItems(
  t: TranslateFn,
  options?: GetDefaultCatalogCarouselItemsOptions
): CatalogCarouselItem[] {
  const items: CatalogCarouselItem[] = [
    {
      // Agentic AI leads after the `agentic-ai-ft-2026` free-trial experiment ended. The flag-on
      // FT carousel item still reuses the same `agentic-ai` theme + bg asset.
      title: 'Build AI systems that act\n**Every agentic AI skill, in one place.**',
      ctaLabel: 'View Program',
      ctaUrl: '/course/agentic-ai--nd900',
      theme: 'agentic-ai',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/agentic-ai-card-bg.png',
      key: CATALOG_AGENTIC_AI_NANODEGREE_CAROUSEL_KEY,
      overlay: true,
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'view_course_clicked',
        value: 'nd900',
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.mastersAi.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.mastersAi.ctaLabel'),
      ctaUrl: '/masters-artificial-intelligence',
      theme: 'woolf-gold',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/woolf-card-bg.png',
      key: 'masters-artificial-intelligence',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'masters_entry_clicked',
        additionalAttributes: {
          formal_degree_key: 'ms_ai',
        },
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.nd608.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.nd608.ctaLabel'),
      ctaUrl: '/course/generative-ai--nd608',
      theme: 'purple',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/purple-card-bg.png',
      key: 'nd608',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'view_course_clicked',
        value: 'nd608',
      },
    },
    {
      title: t('catalog.hero.carousel.carouselItems.nd089.title'),
      ctaLabel: t('catalog.hero.carousel.carouselItems.nd089.ctaLabel'),
      ctaUrl: '/course/ai-programming-python-nanodegree--nd089',
      theme: 'cyan',
      backgroundImage: '/images/experiments/ab_plp_redesign_2025/cyan-card-bg.png',
      key: 'nd089',
      eventPayload: {
        type: 'click' as const,
        name: 'catalogCtaClicked',
        label: 'view_course_clicked',
        value: 'nd089',
      },
    },
  ];

  if (options?.omitAgenticAiNanodegree) {
    return items.filter((item) => item.key !== CATALOG_AGENTIC_AI_NANODEGREE_CAROUSEL_KEY);
  }

  return items;
}
